import { InfiniteData, QueryClient, QueryKey } from '@tanstack/react-query';

import { minutesToHours } from '@/helpers/date-format';
import { PaginatedResponse } from '@/modules/common/types/pagination-types';

import { APPROVALS_QUERY_KEYS } from '../constants/approvals-query-keys';
import { isApprovalTimeEntry } from '../helpers/approval-row-model.helper';
import {
  ApprovalEntryRow,
  ApprovalTimeEntry,
  TimeEntryStatus,
} from '../types/time-tracking-approvals';

type WorklogCache = InfiniteData<PaginatedResponse<ApprovalEntryRow>>;

/** True when a cached leaf row is one of the changed worklog entries (time-off rows never match). */
const isChangedEntry = (item: ApprovalEntryRow, changedIds: Set<number>): boolean =>
  isApprovalTimeEntry(item) && changedIds.has(item.id);

/** The approved minutes an approve request wrote for one entry — one value per purpose. */
export interface PatchedApprovedMinutes {
  invoice: number;
  bill: number;
}

interface PatchApprovalWorklogsParams {
  queryClient: QueryClient;
  /** The entries that were just approved/unapproved. */
  entries: ApprovalTimeEntry[];
  status: TimeEntryStatus;
  approvedMinutesById?: Map<number, PatchedApprovedMinutes>;
}

/**
 * Approve/unapprove changes a worklog's concurrency `hash` on the backend, but the endpoints
 * return `void` (no fresh hash). So we do two things, in order:
 *
 *  1. Optimistically patch the changed rows in place (status + both approved values) for instant UX.
 *  2. Invalidate ONLY the worklog sections that actually contained a changed entry, so they
 *     refetch and pick up the new hashes. Without (2), a follow-up unapprove/approve would send
 *     a stale hash and the backend rejects it with 409 "The worklog data has changed."
 *
 * This stays targeted — only the affected members' worklog pages refetch, never the whole tree
 * — so it's far lighter than invalidating `APPROVALS_QUERY_KEYS.all`.
 */
export const patchApprovalWorklogs = ({
  queryClient,
  entries,
  status,
  approvedMinutesById,
}: PatchApprovalWorklogsParams): void => {
  const changedIds = new Set(entries.map(e => e.id));
  if (changedIds.size === 0) return;

  // Query keys of the worklog sections we touched — invalidated afterwards for fresh hashes.
  const affectedKeys: QueryKey[] = [];

  queryClient.setQueriesData<WorklogCache>(
    { queryKey: APPROVALS_QUERY_KEYS.worklogsAll },
    current => {
      if (!current) return current;

      let changed = false;
      const pages = current.pages.map(page => {
        if (!page.items.some(item => isChangedEntry(item, changedIds))) return page;
        changed = true;
        const items = page.items.map((item): ApprovalEntryRow => {
          // Inline guard (not `isChangedEntry`) so TypeScript narrows `item` to a worklog entry.
          if (!isApprovalTimeEntry(item) || !changedIds.has(item.id)) return item;
          const approvedMinutes = approvedMinutesById?.get(item.id);
          return {
            ...item,
            status,
            approvedForInvoiceHours:
              approvedMinutes !== undefined
                ? minutesToHours(approvedMinutes.invoice)
                : item.approvedForInvoiceHours,
            approvedForBillHours:
              approvedMinutes !== undefined
                ? minutesToHours(approvedMinutes.bill)
                : item.approvedForBillHours,
          };
        });
        return new PaginatedResponse({ items, pagination: page.pagination });
      });

      return changed ? { ...current, pages } : current;
    }
  );

  // Collect the keys that contained a changed entry, then invalidate just those for fresh hashes.
  queryClient
    .getQueryCache()
    .findAll({ queryKey: APPROVALS_QUERY_KEYS.worklogsAll })
    .forEach(query => {
      const data = query.state.data as WorklogCache | undefined;
      const contains = data?.pages.some(page =>
        page.items.some(item => isChangedEntry(item, changedIds))
      );
      if (contains) affectedKeys.push(query.queryKey);
    });

  affectedKeys.forEach(queryKey => {
    void queryClient.invalidateQueries({ queryKey, exact: true });
  });
};
